/* eslint-disable @typescript-eslint/no-throw-literal */
/* eslint-disable prefer-arrow/prefer-arrow-functions */
import { DateTimeSystemPipe } from 'src/app/modules/core/pipes/date-time-system.pipe';
import { COMPANIES_ID } from '../../enumerables/companies-id';
import { IError } from '../../interfaces';
import { ITableTextDataColorConfig } from '../../interfaces/table/table-text-data-color-config';
import { ITableTooltipDynamicConfig } from '../../interfaces/table/table-tooltip-dynamic-config';
import { LanguageService } from '../parameters';
import moment from 'moment';
import { IStatusGroup } from 'src/app/modules/widget/interfaces/status-group';
import { IStatusMachine } from 'src/app/modules/widget/interfaces/status-machine';

/**
 * Provee utilidades generales para la solución
 */

export function toInteger(value: any): number {
  return parseInt(`${value}`, 10);
}

export function toString(value: any): string {
  return value !== undefined && value !== null ? `${value}` : '';
}

export function getValueInRange(value: number, max: number, min = 0): number {
  return Math.max(Math.min(value, max), min);
}

export function isString(value: any): value is string {
  return typeof value === 'string';
}

export function isNumber(value: any): value is number {
  return !isNaN(toInteger(value));
}

export function isInteger(value: any): value is number {
  return typeof value === 'number' && isFinite(value) && Math.floor(value) === value;
}

export function isDefined(value: any): boolean {
  return value !== undefined && value !== null;
}

/**
 * Returns truthy if the path to the object exists
 *
 * @example
 * const Hello = {World: { Foo: { Bar: 'Baz'}}};
 * console.log(objectPathExists(Hello, 'World.Foo.Bar')) // true
 * console.log(objectPathExists(Hello, 'World.Foo.Bar', true)) // Baz
 * console.log(objectPathExists(Hello, 'World.Foo', true)) // {Bar: 'Baz'}
 */
export function objectPathExists(o: object, path: string, getValue?: boolean): boolean | any {
  if (!o || !Object.keys(o).length) {
    return false;
  }
  const keys = path.split('.');
  const loop = () => keys.every((k) => ((o = o[k]), isDefined(o)));
  const passFail = loop();
  return !getValue ? passFail : passFail && o;
}

export function padNumber(value: number): string {
  if (isNumber(value)) {
    return `0${value}`.slice(-2);
  }
  return '';
}

export function regExpEscape(text): string {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

export function hasClassName(element: any, className: string): boolean {
  return element && element.className && element.className.split && element.className.split(/\s+/).indexOf(className) >= 0;
}

export function groupBy(target, funcProp): any {
  return target.reduce((acc, val) => {
    (acc[funcProp(val)] = acc[funcProp(val)] || []).push(val);
    return acc;
  }, {});
}

export function toMs(date) {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes());
}

export function uniqBy(target, key): void {
  const seen = {};
  return target.filter((item) => {
    const k = key(item);
    return seen.hasOwnProperty(k) ? false : (seen[k] = true);
  });
}

export function camelToSnake(value: string): string {
  if (value) {
    // Reemplaza mayúsculas por minúsculas precedidas de un '_' solo si no hay espacios
    // EJ: 'FueraDeServicio' -> 'fuera_de_servicio'
    const parsed = value
      .replace(/([A-Z])/g, (match, p1) => {
        // Si hay un espacio antes del match, solo convertimos a minúscula sin agregar '_'
        // EJ: Fuera de Servicio -> fuera de servicio
        const isPrecededBySpace = value.charAt(value.indexOf(match) - 1) === ' ';
        return isPrecededBySpace ? p1.toLowerCase() : `_${p1.toLowerCase()}`;
      })
      .replace(/^_/, ''); // Elimina cualquier '_' inicial, si es que quedó
    return parsed;
  }

  // TODO Error a config
  throw { message: `camelToSnake: provided_value_not_valid` } as IError;
}

export function camelToKebab(value: string): string {
  if (value) {
    const parsed = value.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
    return parsed;
  }
  // TODO Error a config
  throw { message: `camelToKebab: provided_value_not_valid` } as IError;
}

export function pascalToKebab(value: string): string {
  if (value) {
    value = firstLetterLower(value);
    const parsed = value.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
    return parsed;
  }
  // TODO Error a config
  throw { message: `camelToKebab: provided_value_not_valid` } as IError;
}

export function firstLetterUpper(value: string): string {
  if (value && typeof value === 'string') {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  return value;
}

export function firstLetterLower(value: string): string {
  if (value && typeof value === 'string') {
    return value.charAt(0).toLocaleLowerCase() + value.slice(1);
  }
  return value;
}

export function isEquivalent(a: { [key: string]: any }, b: { [key: string]: any }) {
  const aProps = Object.getOwnPropertyNames(a);
  const bProps = Object.getOwnPropertyNames(b);
  if (aProps.length !== bProps.length) {
    return false;
  }
  for (let i = 0; i < aProps.length; i++) {
    const propName = aProps[i];
    if (a[propName] !== b[propName]) {
      return false;
    }
  }
  return true;
}

/**
 * Function to convert an Hexadecimal to Rgba
 *
 * @param hex Hexadecimal color
 * @param alpha Opacity
 */
export function hexToRGB(hex: string, alpha: string): string {
  hex = hex || '#fff200';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  if (alpha) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Function to parse milliseconds to HH:MM:SS
 *
 * @param ms Milliseconds to transform
 */
export function msToHms(ms: number): string {
  if (typeof ms !== 'number' || Number.isNaN(ms)) {
    throw new TypeError('Expected a number');
  }
  const sign = ms < 0 ? '-' : '';
  const abs = Math.abs(ms);
  let h: number | string = Math.floor(abs / 3600000);
  const m = `0${Math.floor(abs / 60000) % 60}`.slice(-2);
  const s = `0${Math.floor(abs / 1000) % 60}`.slice(-2);
  h = h < 10 ? `0${h}` : h;
  return `${sign}${h}:${m}:${s}`;
}

/**
 * Function sort an array by key
 *
 * @param array Array to sort
 * @param key Key to sort array
 */
export function sortByKey(array: any[], key: string, order: string = 'asc', isDate?: boolean): any[] {
  const datePipe = new DateTimeSystemPipe();
  array.sort((a, b) => {
    const keyA = isDate ? datePipe.transform(a[key]) : a[key];
    const keyB = isDate ? datePipe.transform(b[key]) : b[key];

    if (order === 'asc') {
      if (keyA < keyB) {
        return -1;
      }
      if (keyA > keyB) {
        return 1;
      }
      return 0;
    }
    if (keyA > keyB) {
      return -1;
    }
    if (keyA < keyB) {
      return 1;
    }
    return 0;
  });
  return array;
}

/**
 * Function to get unique values in array
 *
 * @param array Array data
 * @param key key to search unique
 */
export function getUniqueValues(array: any[], key: string): any[] {
  const uniqueValues = [];

  for (const data of array) {
    const foundData = uniqueValues.find((v) => v[key] === data[key]);
    if (!foundData) {
      uniqueValues.push(data);
    }
  }

  return uniqueValues;
}

export const compare = (v1, v2) => (v1 < v2 ? -1 : v1 > v2 ? 1 : 0);

export const compareDate = (v1, v2) => {
  const dateA = new Date(v1).getTime();
  const dateB = new Date(v2).getTime();
  return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
};

export const numberOnly = (event): boolean => {
  const charCode = event.which ? event.which : event.keyCode;
  if (charCode > 31 && (charCode < 48 || charCode > 57)) {
    return false;
  }
  return true;
};

/**
 * Filter array of objects
 *
 * @param items List of items to filter
 * @param term  a string term to compare with every property of the list
 * @param excludes List of keys which will be ignored during search
 *
 */
export function filter(items: Array<{ [key: string]: any }>, term: string, excludes: any): Array<{ [key: string]: any }> {
  const toCompare = term.toLowerCase();

  function checkInside(item: any, term: string) {
    if (typeof item === 'string' && item.toString().toLowerCase().includes(toCompare)) {
      return true;
    }

    for (const property in item) {
      if (item[property] === null || item[property] == undefined || excludes.includes(property)) {
        continue;
      }
      if (typeof item[property] === 'object') {
        if (checkInside(item[property], term)) {
          return true;
        }
      } else if (item[property].toString().toLowerCase().includes(toCompare)) {
        return true;
      }
    }
    return false;
  }

  return items.filter(function (item) {
    return checkInside(item, term);
  });
}

export function fixTimePresentation(time: number) {
  if (time < 10) {
    return `0${time}`;
  }

  return time;
}

export function flatten(arr) {
  return arr.reduce(function (flat, toFlatten) {
    return flat.concat(Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten);
  }, []);
}

export function translateMachineName(machineTypeName: string, companyId: string | number): string {
  const translatableCompanies = [COMPANIES_ID.HCM];
  if (machineTypeName === 'acuñador') {
    return 'acunador';
  }
  if (!translatableCompanies.includes(+companyId)) {
    return machineTypeName.toLowerCase();
  }

  switch (machineTypeName.toLowerCase()) {
    case 'jumbo':
    case 'equipo de perforación':
      return 'jumbo';
    case 'scoop':
    case 'lhd':
      return 'lhd';
    case 'truck':
    case 'volquete':
      return 'truck';
    default:
      return machineTypeName;
  }
}

/**
 * Parse string to tag format.
 *
 * @param value string i.e. '000.000' or '000000'
 * @returns '000.000'
 */
export function parseTag(value: string): string {
  if (value) {
    value = value.replace(/[^0-9]/g, '');
    if (value.length >= 7 && value.indexOf('.') === -1) {
      value = value.slice(0, value.length - 1);
    }
    if (value.length > 3 && value.indexOf('.') === -1) {
      value = [value.slice(0, 3), '.', value.slice(3, 6)].join('');
    }
  }
  if (value && value.length > 3 && value.indexOf('.') === -1) {
    value = [value.slice(0, 3), '.', value.slice(3, 6)].join('');
  }

  return value;
}

/**
 * Function to remove accent marks
 *
 * @param text String to
 * @returns Return string without accent marks
 */
export const removeAccentMark = (text: string): string => {
  return text
    .replace(/á/gi, 'a')
    .replace(/é/gi, 'e')
    .replace(/í/gi, 'i')
    .replace(/ó/gi, 'o')
    .replace(/ú/gi, 'u')
    .replace(/Á/gi, 'A')
    .replace(/É/gi, 'E')
    .replace(/Í/gi, 'I')
    .replace(/Ó/gi, 'O')
    .replace(/Ú/gi, 'U')
    .replace(/ñ/gi, 'n')
    .replace(/Ñ/gi, 'N');
};

/**
 * Validates KeyboardEvent key with regex.
 */
export function validateInputWithRegex($event: KeyboardEvent, regex: RegExp | string, match = true): void {
  const matchCondition = match ? $event.key.match(regex) : !$event.key.match(regex);
  if (matchCondition && $event.key !== 'Backspace') {
    $event.preventDefault();
  }
}

export const createTimeByShift = (shiftTime: { hours: number; minutes: number; seconds: number }) => {
  const newTime = { hours: '', minutes: '', seconds: '' };
  newTime.hours = shiftTime.hours < 10 ? `0${shiftTime.hours}` : String(shiftTime.hours);
  newTime.minutes = shiftTime.minutes < 10 ? `0${shiftTime.minutes}` : String(shiftTime.minutes);
  newTime.seconds = shiftTime.seconds < 10 ? `0${shiftTime.seconds}` : String(shiftTime.seconds);
  return newTime;
};

export const createHourByShift = (shiftTime: { hours: number; minutes: number; seconds: number }) => {
  const newTime = createTimeByShift(shiftTime);
  return `${newTime.hours}:${newTime.minutes}:${newTime.seconds}`;
};

export const isTimeBetween = (startTime: string, endTime: string, currentTime: string) => {
  const auxStartTime = '00:00:00';
  const auxEndTime = '23:59:59';
  let isBetween = false;
  if (startTime > endTime) {
    isBetween = (currentTime >= startTime && currentTime <= auxEndTime) || (currentTime <= endTime && currentTime >= auxStartTime);
  } else {
    isBetween = currentTime >= startTime && currentTime <= endTime;
  }
  return isBetween;
};

/**
 * Validate only number
 */
export function validateNumber($event: KeyboardEvent): void {
  if ($event.key.match(this.REGEX_ONLYNUMBER) && $event.key !== 'Backspace') {
    $event.preventDefault();
  }
}

/**
 * Function to capitalize phrase
 *
 * @param phrase phrase
 * @returns phrase capitalized
 */
export function capitalizeAll(phrase: string): string {
  phrase = phrase.toLowerCase();
  const words = phrase.split(' ');
  const newChain = words.map((v) => firstLetterUpper(v));

  return newChain.join(' ');
}

/**
 * Capitaliza la primera letra de cada palabra en un string.
 *
 * @param name - El nombre a capitalizar
 * @returns El nombre con la primera letra de cada palabra en mayúscula
 */
export function capitalizeParentStatusName(name: string): string {
  return name.replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Function to transform DD/MM/YYYY HH:mm:ss to YYYY-MM-DDTHH:mm:ss
 *
 * @param date Date to renew
 */
export const renewDateDDMMYYYY = (oldDate: string): string => {
  const date = oldDate.split(' ')[0];
  const time = oldDate.split(' ')[1];
  const newDate = String(date).split('/').reverse().join('-');
  return `${newDate}T${time}.000`;
};

export const uniqueArrayData = (value, index, self) => {
  return self.indexOf(value) === index;
};

export const makeBadgeConfig = (data: any) => {
  const badgeConfig = [];
  if (data.length > 0) {
    data.forEach((d) => {
      const status = d.statusName || d.status;
      if (d.statusColor && !badgeConfig.some((x) => x.badgeName === status)) {
        badgeConfig.push({
          class: 'badge badge-primary',
          color: status === '-' ? 'unset' : d.secondColor,
          backgroundColor: d.status === '-' ? 'unset' : d.thirdColor,
          badgeName: status,
          badgeNameParent: d.parentStatus,
          width: 'auto',
          height: '24px',
          border: d.parentStatus?.toLowerCase() !== 'sin estado' ? `1px solid ${d.secondColor}` : '',
          fontSize: '12px',
          fontWeight: d.parentStatus?.toLowerCase() !== 'sin estado' ? '600' : '500',
        });
      }
    });
  }
  return badgeConfig;
};

export const isOutOfParkConfigColor = (data: any, field: string) => {
  const colorsConfig = [];
  if (data.length > 0) {
    data.forEach((d) => {
      colorsConfig.push({
        field,
        color: d.isOutOfPark ? '#F05050' : 'default',
        isBold: d.isOutOfPark,
      });
    });
  }
  return colorsConfig as ITableTextDataColorConfig[];
};

export const isOutOfParkConfigTooltip = (data: any, field: string, langService: LanguageService): ITableTooltipDynamicConfig[] => {
  const tooltipConfig = [];
  if (data.length > 0) {
    data.forEach((d) => {
      tooltipConfig.push({
        field,
        tooltip: d.isOutOfPark ? `${langService.message('outside-park')}\n${d[field]}` : '',
      });
    });
  }
  return tooltipConfig;
};

export const formatNameOperator = (fullName: string) => {
  const nameOperator = fullName?.split(' ');
  if (nameOperator.length === 1) {
    return fullName;
  }
  const lastPosition = nameOperator[nameOperator.length - 1];
  const convertNameOperator = `${lastPosition.charAt(0)}.`;
  nameOperator[nameOperator.length - 1] = convertNameOperator;
  return nameOperator.join(' ');
};

export const getUnits = (machineTypeName: string, countProduction: number, langService: LanguageService): string => {
  const unitsMap = {
    LHD: [langService.message('shelve'), langService.message('shelves')],
    lhd: [langService.message('shelve'), langService.message('shelves')],
    Lhd: [langService.message('shelve'), langService.message('shelves')],
    Truck: [langService.message('transfers'), langService.message('loops')],
    truck: [langService.message('transfers'), langService.message('loops')],
    Jumbo: [langService.message('drill'), langService.message('perforated_mts')],
  };

  const [singular, plural] = unitsMap[machineTypeName] || ['', ''];
  return countProduction === 0 || countProduction > 1 ? firstLetterUpper(plural) : firstLetterUpper(singular);
};

/**
 * Function to group the status by machine with substatus
 *
 * @param groupedStatus Status grouped
 */
export const groupStatusByMachine = (machine: IStatusMachine[]): IStatusGroup[] => {
  let groupByStatus = groupBy(machine, (x: IStatusMachine) => x.parentStatusName);
  groupByStatus = Object.keys(groupByStatus).map((key) => groupByStatus[key]);
  const statusData: IStatusGroup[] = [];
  for (const machineStatus of groupByStatus as IStatusMachine[][]) {
    // Iteration by each status (not machine's detail)
    let imageName = '';
    if (machineStatus[0].parentStatusImage) {
      imageName = machineStatus[0].parentStatusImage;
    } else {
      imageName = machineStatus[0].parentStatusName.toLowerCase().replace(/ /gi, '_');
    }

    const statusDetail: IStatusGroup = {
      machineName: machineStatus[0].machineName,
      name: machineStatus[0].parentStatusName,
      milliseconds: 0,
      color: machineStatus[0].color,
      statusInside: [],
      imageName,
      parentStatusName: machineStatus[0].parentStatusName,
      totalDifference: machineStatus[0].shiftTimeDifference,
    };
    for (const detail of machineStatus) {
      // Iteration by machine's detail
      const statusDiff = moment.duration(moment(detail.nextCreatedAt).diff(moment(detail.createdAt))).asMilliseconds();
      const foundSubStatus = statusDetail.statusInside.find((status) => status.name === detail.statusName);
      // If the substatus is found, just add the milliseconds, else, create a new object of substatus
      if (foundSubStatus) {
        foundSubStatus.milliseconds += statusDiff;
      } else {
        const subStatus: IStatusGroup = {
          machineName: statusDetail.machineName,
          imageName: detail.statusImage,
          name: detail.statusName,
          milliseconds: statusDiff,
          parentStatusName: detail.parentStatusName,
          color: detail.color,
          totalDifference: detail.shiftTimeDifference,
        };
        statusDetail.statusInside.push(subStatus);
      }
      statusDetail.milliseconds += statusDiff;
    }
    statusData.push(statusDetail);
  }
  return statusData;
};
